import React, { createContext, useState, ReactNode, useEffect, useMemo, useCallback } from "react";
import { tokenDecode, tokenDecodeSafe } from "../utils/helper";

export interface DecodedData {
  exp: number;
  categories?: string[];
  [key: string]: any;
}

interface TokenContextType {
  token: string;
  decodedData: DecodedData | null;
  isFreePlan: boolean;
  isHomePlan: boolean;
  isShopPlan: boolean;
  isEmployee: boolean;
  tokenServiceCategory: any;
  updateToken: (newToken: string | null) => void;
}

export const TokenContext = createContext<TokenContextType>({
  token: "",
  decodedData: null,
  isFreePlan: false,
  isHomePlan: false,
  isShopPlan: false,
  isEmployee: false,
  tokenServiceCategory: null,
  updateToken: () => {},
});

interface ProviderProps {
  children: ReactNode;
}

export const TokenProvider: React.FC<ProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string>(
    () => localStorage.getItem("us_st_d") || "",
  );
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [isHomePlan, setIsHomePlan] = useState(false);
  const [isShopPlan, setIsShopPlan] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [tokenServiceCategory, setTokenServiceCategory] = useState<any>(null);

  useEffect(() => {
    const checkAndClearOldToken = () => {
      const token = localStorage.getItem("us_st_d");
      if (!token) return;

      try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const decoded = JSON.parse(atob(base64));

        if (decoded?.data?.user?.role_data?.category_permissions) {
          localStorage.removeItem("us_st_d");
          setToken("");
          setDecodedData(null);
          // Force reload to clear state
          window.location.reload();
        }
      } catch (error) {
        // If token is invalid, clear it
        localStorage.removeItem("us_st_d");
        setToken("");
      }
    };

    checkAndClearOldToken();
  }, []); 

  const normalizePlanKey = (value = "") =>
    String(value).toLowerCase().replace(/\s+/g, "").trim();

  const sanitizeDecoded = (decoded: DecodedData | null): DecodedData | null => {
    if (!decoded) return null;

    const sanitized = { ...decoded };

    if (sanitized?.data?.user?.role_data) {
      const { category_permissions, ...roleDataRest } =
        sanitized.data.user.role_data;
      sanitized.data = {
        ...sanitized.data,
        user: {
          ...sanitized.data.user,
          role_data: roleDataRest,
        },
      };
    }

    return sanitized;
  };

  const computePlanFlags = (decoded: DecodedData | null) => {
    if (!decoded) {
      setIsFreePlan(false);
      setIsHomePlan(false);
      setIsShopPlan(false);
      setIsEmployee(false);
      setTokenServiceCategory(null);
      return;
    }

    const decodedVendorData = decoded?.data?.vendor;

    if (!decodedVendorData) {
      setIsFreePlan(false);
      setIsHomePlan(false);
      setIsShopPlan(false);
      setIsEmployee(false);
      setTokenServiceCategory(null);
      return;
    }

    const isStaff = decoded?.data?.user?.role_data?.name === "executive";


    const subscriptionPlan = decodedVendorData?.subscriptionPlan?.[0];
    const planName =
      subscriptionPlan?.plan_name ||
      subscriptionPlan?.planname ||
      subscriptionPlan?.name ||
      decodedVendorData?.accountType ||
      "";

    const planKey = normalizePlanKey(planName);

    setIsFreePlan(planKey === "freeforever" || planName === "Free Forever");
    setIsHomePlan(planKey.includes("home"));
    setIsShopPlan(planKey.includes("shop") || planKey.includes("care"));
    setIsEmployee(isStaff);

    if (isStaff) {
      // Add safety checks for undefined arrays
      const serviceCategory = decodedVendorData?.service_category || [];
      const employeeServiceCategory =
        decodedVendorData?.employeeServiceCategory || [];

      // Only try to filter if arrays exist and have length
      if (serviceCategory.length > 0 && employeeServiceCategory.length > 0) {
        const employeeCategoryIds = new Set(
          employeeServiceCategory.map(
            (item: any) => item.service_category_id
          )
        );
        const filtered = serviceCategory.filter((category: any) =>
          employeeCategoryIds.has(category.service_category_id)
        );
        setTokenServiceCategory(filtered);
      } else {
        setTokenServiceCategory([]);
      }
    } else {
      setTokenServiceCategory(decodedVendorData?.service_category || []);
    }
  };
  const [decodedData, setDecodedData] = useState<DecodedData | null>(() => {
    if (token) {
      const decoded = sanitizeDecoded(tokenDecode(token));
          console.log(
            "has category_permissions?",
            !!decoded?.data?.user?.role_data?.category_permissions,
          );

      computePlanFlags(decoded);
      return decoded || null;
    }
    return null;
  });

  const updateToken = useCallback((newToken: string | null) => {
    if (!newToken) {
      setToken("");
      setDecodedData(null);
      computePlanFlags(null);
      localStorage.removeItem("us_st_d");
      return;
    }

    localStorage.setItem("us_st_d", newToken);
    setToken(newToken);

    const decoded = sanitizeDecoded(tokenDecodeSafe(newToken));
    setDecodedData(decoded);
    computePlanFlags(decoded);
  }, []);

  const value = useMemo(
    () => ({
      token,
      decodedData,
      isFreePlan,
      isHomePlan,
      isShopPlan,
      isEmployee,
      tokenServiceCategory,
      updateToken,
    }),
    [
      
      token,
      decodedData,
      isFreePlan,
      isHomePlan,
      isShopPlan,
      isEmployee,
      tokenServiceCategory,
      updateToken,
    ]
  );

  return (
    <TokenContext.Provider
      value={value}
    >
      {children}
    </TokenContext.Provider>
  );
};;;
